import { Button, Card, CardContent, Input, Label, Spinner } from '@crm/ui';
import { Pencil, Plus, RefreshCw, Send, ShieldCheck } from 'lucide-react';
import { useState } from 'react';
import { Autocomplete } from '@/components';
import { toErrorMessage } from '@/modules/auth/utils';
import { useCoupons } from '@/modules/coupons/api';
import {
  type MessageTemplate,
  useMessageTemplatesByEvent,
  useSyncApproval,
} from '@/modules/messages/api';
import { useTestCampaign } from '../api';
import { TemplateModal } from './template-modal';
import { ApprovalChip, TemplateStatus } from './template-status';
import { WhatsappBubble } from './whatsapp-bubble';

interface MessageTabProps {
  campaignId: string;
  body: string;
  couponId: string | null;
  templateId: string | null;
  /** Vira o nome sugerido quando o modelo nasce daqui. */
  campaignName: string;
  /** A hora em que a mensagem pode sair - `HH:mm`. */
  sendWindowStart: string | null;
  sendWindowEnd: string | null;
  onChange: (values: {
    body?: string;
    couponId?: string | null;
    templateId?: string | null;
    sendWindowStart?: string | null;
    sendWindowEnd?: string | null;
  }) => void;
  readOnly: boolean;
}

/**
 * O passo da mensagem e o proprio modelo: a campanha nao tem um texto de um
 * lado e um modelo aprovado do outro - o que a Meta aprova e o que sai.
 */
export function MessageTab({
  campaignId,
  body,
  couponId,
  templateId,
  campaignName,
  sendWindowStart,
  sendWindowEnd,
  onChange,
  readOnly,
}: MessageTabProps) {
  const coupons = useCoupons({ page: 1, perPage: 50, status: 'active' });
  const templates = useMessageTemplatesByEvent('CAMPAIGN');
  const sincroniza = useSyncApproval();
  const testar = useTestCampaign(campaignId);
  const [editando, setEditando] = useState<MessageTemplate | null>(null);
  const [aberto, setAberto] = useState(false);

  const cupom = coupons.data?.data.find((item) => item.id === couponId) ?? null;
  const escolhido = cupom ? { id: cupom.id, label: cupom.code } : null;

  const modelos = templates.data?.data ?? [];
  const modelo = modelos.find((item) => item.id === templateId) ?? null;

  // Sem modelo escolhido, o que se ve e o texto que a campanha ja tinha.
  const texto = modelo?.body ?? body;

  /** Escolher um modelo traz o texto dele junto: e ele que vai ao cliente. */
  function escolhe(id: string | null) {
    const novo = modelos.find((item) => item.id === id) ?? null;

    onChange({ templateId: id, ...(novo ? { body: novo.body } : {}) });
  }

  function abrir(template: MessageTemplate | null) {
    setEditando(template);
    setAberto(true);
  }

  return (
    <Card variant="flush">
      <CardContent className="space-y-5 py-5">
        <div className="space-y-3">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <Label>Modelo da mensagem</Label>
            <Button size="sm" disabled={readOnly} onClick={() => abrir(null)}>
              <Plus className="h-4 w-4" />
              Novo modelo
            </Button>
          </div>

          {/* A regra e de fora, e vale destaque: sem saber disso, a pessoa acha
              que foi o disparo que quebrou. */}
          <div className="flex gap-2.5 rounded-card border border-primary/20 bg-primary/5 p-3">
            <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
            <p className="text-xs leading-relaxed text-muted-foreground">
              <span className="font-medium text-foreground">
                A Meta so permite disparar mensagens em massa com modelos aprovados.
              </span>{' '}
              Selecione ou crie um modelo para esta campanha, para solicitarmos a aprovacao.
            </p>
          </div>

          {templates.isPending ? (
            <div className="flex justify-center py-4">
              <Spinner />
            </div>
          ) : (
            <Autocomplete
              placeholder="Buscar modelo..."
              emptyLabel="Sem modelo"
              limit={10}
              disabled={readOnly}
              options={modelos.map((item) => ({
                id: item.id,
                label: item.name,
                hint: <ApprovalChip approval={item.approval} />,
              }))}
              value={modelo ? [{ id: modelo.id, label: modelo.name }] : []}
              onChange={(opcoes) => escolhe(opcoes[0]?.id ?? null)}
            />
          )}

          <div className="flex flex-wrap items-center gap-2">
            <TemplateStatus template={modelo} />

            {/* A Meta nao avisa quando decide: a varredura pergunta de hora em
                hora, e este botao pergunta agora. */}
            {modelo?.approval === 'PENDING' ? (
              <Button
                size="sm"
                variant="ghost"
                disabled={sincroniza.isPending}
                onClick={() => sincroniza.mutate(modelo.id)}
              >
                {sincroniza.isPending ? <Spinner /> : <RefreshCw className="h-4 w-4" />}
                Atualizar
              </Button>
            ) : null}
          </div>
        </div>

        {texto ? (
          <div className="space-y-1.5">
            <Label>Como chega no WhatsApp</Label>
            <WhatsappBubble body={texto} coupon={cupom} />

            {/* Mexer no texto se pede depois de le-lo: o botao fica onde a
                vontade de mudar aparece, e nao no cabecalho. */}
            {modelo ? (
              <div className="flex justify-end pt-1">
                <Button
                  size="sm"
                  variant="outline"
                  disabled={readOnly}
                  onClick={() => abrir(modelo)}
                >
                  <Pencil className="h-4 w-4" />
                  Editar modelo
                </Button>
              </div>
            ) : null}
          </div>
        ) : null}
        {/* Ninguem quer promocao as tres da manha: fora da janela a campanha
            espera a proxima varredura dentro dela. */}
        <div className="space-y-2">
          <Label>Horario em que a mensagem pode sair</Label>
          <div className="flex items-center gap-2">
            <Input
              type="time"
              className="w-32"
              aria-label="Inicio da janela"
              disabled={readOnly}
              value={sendWindowStart ?? ''}
              onChange={(event) => onChange({ sendWindowStart: event.target.value || null })}
            />
            <span className="text-sm text-muted-foreground">as</span>
            <Input
              type="time"
              className="w-32"
              aria-label="Fim da janela"
              disabled={readOnly}
              value={sendWindowEnd ?? ''}
              onChange={(event) => onChange({ sendWindowEnd: event.target.value || null })}
            />
          </div>
        </div>

        <div className="space-y-1.5">
          <Autocomplete
            label="Cupom da campanha"
            placeholder="Buscar cupom..."
            emptyLabel="Sem cupom"
            disabled={readOnly}
            options={(coupons.data?.data ?? []).map((coupon) => ({
              id: coupon.id,
              label: coupon.code,
            }))}
            value={escolhido ? [escolhido] : []}
            onChange={(opcoes) => onChange({ couponId: opcoes[0]?.id ?? null })}
          />
          <p className="text-xs text-muted-foreground">
            O codigo e o desconto entram no lugar das marcacoes "Codigo do cupom" e "Desconto do
            cupom".
          </p>
        </div>

        {/* Ler o texto na tela nao e a mesma coisa que receber: e no WhatsApp
            que se ve a quebra de linha no lugar errado. */}
        <div className="flex flex-wrap items-center gap-3">
          <Button
            variant="outline"
            disabled={readOnly || testar.isPending}
            onClick={() => testar.mutate()}
          >
            {testar.isPending ? <Spinner /> : <Send className="h-4 w-4" />}
            Enviar teste para mim
          </Button>

          {testar.data ? (
            <p className="text-sm text-muted-foreground">Enviado para {testar.data.to}.</p>
          ) : null}
          {testar.error ? (
            <p className="text-sm text-destructive">{toErrorMessage(testar.error)}</p>
          ) : null}
        </div>
      </CardContent>

      {aberto ? (
        <TemplateModal
          template={editando}
          sugestaoDeNome={campaignName}
          coupon={cupom}
          onClose={() => setAberto(false)}
          onSaved={(salvo) => {
            setAberto(false);
            // Modelo novo ja entra escolhido: quem acabou de escrever a frase
            // nao deveria ter de procura-la na lista em seguida.
            onChange({ templateId: salvo.id, body: salvo.body });
          }}
        />
      ) : null}
    </Card>
  );
}
